import React, { useRef } from 'react';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import usePreferredLanguage from '../utilities/usePreferredLanguage';
import { translateUiText } from '../utilities/siteTranslations';

/**
 * Simple photo uploader that:
 * - Lets user pick or capture an image (accept=\"image/*\" capture=\"environment\")
 * - Converts it to a data URL and passes it to onChange(value)
 * - Shows a small preview if value is present
 *
 * Props:
 * - label: string (field label)
 * - required: boolean
 * - value: string (current data URL / URL)
 * - onChange: (value: string) => void
 * - helperText: string (optional)
 */
const PhotoUploader = ({ label, required, value, onChange, helperText, disabled, maxUploadBytes = 1.5 * 1024 * 1024, maxDimension = 1600, forceJpeg = false, onProcessingChange }) => {
    const galleryInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const preferredLanguage = usePreferredLanguage();
    const t = (text) => translateUiText(text, preferredLanguage);
    const isHeicLikeFile = (file) => {
        if (!file) return false;
        const type = String(file.type || '').toLowerCase();
        const name = String(file.name || '').toLowerCase();
        return type.includes('heic') || type.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif');
    };

    const formatBytes = (bytes) => {
        if (!bytes || Number.isNaN(bytes)) return '0 B';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const estimateDataUrlSize = (dataUrl) => {
        if (!dataUrl || typeof dataUrl !== 'string') return 0;
        const parts = dataUrl.split(',');
        if (parts.length < 2) return 0;
        const base64Length = parts[1].length;
        return Math.ceil((base64Length * 3) / 4);
    };

    const readFileAsDataURL = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === 'string') resolve(reader.result);
                else reject(new Error(t('Could not read file')));
            };
            reader.onerror = () => reject(new Error(t('Could not read file')));
            reader.readAsDataURL(file);
        });

    const convertHeicFile = async (file) => {
        const { default: heic2any } = await import('heic2any');
        const convertedBlob = await heic2any({
            blob: file,
            toType: 'image/jpeg',
            quality: 0.9
        });
        const normalizedBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        const convertedName = String(file.name || 'upload')
            .replace(/\.(heic|heif)$/i, '')
            .concat('.jpg');

        return new File([normalizedBlob], convertedName, {
            type: 'image/jpeg',
            lastModified: Date.now()
        });
    };

    const loadImage = (src) =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error(t('Could not load image')));
            image.src = src;
        });

    const compressImage = async (file) => {
        const normalizedFile = isHeicLikeFile(file) ? await convertHeicFile(file) : file;
        const originalDataUrl = await readFileAsDataURL(normalizedFile);
        const image = await loadImage(originalDataUrl);

        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        const largestSide = Math.max(width, height);
        const needsResize = largestSide > maxDimension;
        const needsCompression = normalizedFile.size > maxUploadBytes || forceJpeg;

        if (!needsResize && !needsCompression) {
            return originalDataUrl;
        }

        const scale = needsResize ? maxDimension / largestSide : 1;
        const targetWidth = Math.max(1, Math.round(width * scale));
        const targetHeight = Math.max(1, Math.round(height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) return originalDataUrl;

        if (forceJpeg) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, targetWidth, targetHeight);
        }
        ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

        let quality = 0.85;
        let resizeRatio = 1;
        let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

        while (estimateDataUrlSize(compressedDataUrl) > maxUploadBytes && quality > 0.15) {
            quality -= 0.1;
            compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        }

        while (estimateDataUrlSize(compressedDataUrl) > maxUploadBytes && resizeRatio > 0.45) {
            resizeRatio -= 0.1;
            const reducedWidth = Math.max(1, Math.round(targetWidth * resizeRatio));
            const reducedHeight = Math.max(1, Math.round(targetHeight * resizeRatio));
            canvas.width = reducedWidth;
            canvas.height = reducedHeight;
            ctx.drawImage(image, 0, 0, reducedWidth, reducedHeight);

            let retryQuality = Math.min(quality, 0.7);
            compressedDataUrl = canvas.toDataURL('image/jpeg', retryQuality);
            while (estimateDataUrlSize(compressedDataUrl) > maxUploadBytes && retryQuality > 0.1) {
                retryQuality -= 0.1;
                compressedDataUrl = canvas.toDataURL('image/jpeg', retryQuality);
            }
            quality = retryQuality;
        }

        if (estimateDataUrlSize(compressedDataUrl) > maxUploadBytes) {
            throw new Error('IMAGE_TOO_LARGE');
        }

        return compressedDataUrl;
    };

    const triggerInput = (inputRef) => {
        if (disabled || !inputRef?.current) return;
        inputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        if (!file.type || !file.type.startsWith('image/')) {
            toast.warn(t('Only image files are allowed.'));
            e.target.value = '';
            return;
        }

        onProcessingChange?.(true);
        try {
            const processedDataUrl = await compressImage(file);
            onChange(processedDataUrl);
        } catch (error) {
            const message = String(error?.message || '');
            if (message.includes('decode') || message.includes('load image') || message.includes('Could not load image')) {
                toast.warn(t('This phone photo format could not be processed. Please try another photo or retake it.'));
            } else if (message === 'IMAGE_TOO_LARGE') {
                toast.warn(`${t('Photo size limit reached. Maximum')} ${formatBytes(maxUploadBytes)} ${t('per photo.')}`);
            } else {
                toast.warn(t('Photo could not be processed. Please try again with another photo.'));
            }
        } finally {
            onProcessingChange?.(false);
            // Allows selecting the same file again after a failed attempt.
            e.target.value = '';
        }
    };

    return (
        <div className="flex flex-column gap-2">
            {label && (
                <label className="block mb-1">
                    {label} {required && <span style={{ color: 'red' }}>*</span>}
                </label>
            )}

            <div className="photo-uploader-actions flex gap-2 align-items-center">
                <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <Button
                    type="button"
                    label={t('Take Photo')}
                    icon="pi pi-camera"
                    className="p-button-sm p-button-primary"
                    onClick={() => triggerInput(cameraInputRef)}
                    disabled={disabled}
                />
                <Button
                    type="button"
                    label={value ? t('Choose Another') : t('Choose from Gallery')}
                    icon="pi pi-images"
                    className="p-button-sm p-button-outlined"
                    onClick={() => triggerInput(galleryInputRef)}
                    disabled={disabled}
                />
                {value && (
                    <span className="text-500 text-xs">
                        {t('Photo selected')}
                    </span>
                )}
            </div>

            {helperText && (
                <small className="text-500">
                    {helperText}
                </small>
            )}

            {value && (
                <div style={{ marginTop: '0.5rem' }}>
                    <img
                        src={value}
                        alt={t('Preview')}
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px', border: '1px solid rgba(148,163,184,0.4)' }}
                    />
                </div>
            )}
        </div>
    );
};

export default PhotoUploader;
